import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '../../api/client';
import { useLiveChanges } from '../../live/live';
import { vjtApi, type ProjectTree } from '../../proposals/vue-jalons-taches/api';
import type { HomeProjectDetail } from '../../types';

// A sponsored project's data (milestones/tasks tree + home detail), cached per
// project for the session so switching between projects is instant. While a
// project loads for the first time, the one already on screen stays there.

export interface HomeProjectData { tree: ProjectTree; detail: HomeProjectDetail }

const cache = new Map<number, HomeProjectData>();
const inflight = new Map<number, Promise<HomeProjectData>>();
const reloaders = new Set<() => void>();

/** After a change made elsewhere on the page (the task side pane): forget every project and reload the one shown. */
export function invalidateHomeProjects() {
  cache.clear();
  reloaders.forEach(reload => reload());
}

function fetchProject(id: number): Promise<HomeProjectData> {
  const running = inflight.get(id);
  if (running) return running;
  const p = Promise.all([vjtApi.getTree(id), api.getHomeProject(id)])
    .then(([tree, detail]) => { const data = { tree, detail }; cache.set(id, data); return data; })
    .finally(() => inflight.delete(id));
  inflight.set(id, p);
  return p;
}

/** Load a project ahead of a click (hover, or quietly after the page settles). */
export function prefetchHomeProject(id: number): Promise<void> {
  if (cache.has(id)) return Promise.resolve();
  return fetchProject(id).then(() => undefined, () => undefined);
}

/**
 * The data to draw for `id`. `shownId` is the project the data belongs to: it
 * lags behind `id` while a not-yet-cached project loads (`loading`).
 */
export function useHomeProject(id: number) {
  const [state, setState] = useState<{ shownId: number; data: HomeProjectData | null; error: string | null }>(
    () => ({ shownId: id, data: cache.get(id) ?? null, error: null }));
  const wanted = useRef(id);
  wanted.current = id;

  const load = useCallback((force = false) => {
    if (force) cache.delete(id);
    fetchProject(id)
      .then(data => { if (wanted.current === id) setState({ shownId: id, data, error: null }); })
      .catch((e: Error) => { if (wanted.current === id) setState(s => ({ ...s, shownId: id, error: e.message })); });
  }, [id]);

  useEffect(() => {
    const cached = cache.get(id);
    if (cached) setState({ shownId: id, data: cached, error: null });
    // Cached or not, refresh it: what is shown is at most one load old.
    load(true);
  }, [id, load]);

  useLiveChanges((c) => { if (!c.projectId || c.projectId === id) load(true); });
  useEffect(() => {
    const reload = () => load(true);
    reloaders.add(reload);
    return () => { reloaders.delete(reload); };
  }, [load]);

  return {
    shownId: state.shownId,
    data: state.data,
    loading: state.shownId !== id || !state.data,
    error: state.shownId === id ? state.error : null,
    reload: () => load(true),
  };
}
